import { useEffect, useState } from "react";
import { clsx } from "clsx";
import { useQueryClient } from "@tanstack/react-query";
import { BookOpen, Download, FileDown } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/Button";
import { FileDropZone } from "@/components/ui/FileDropZone";
import { ManagedFileLink } from "@/components/ui/ManagedFileLink";
import { SectionCard } from "@/features/power_projects/components/ui";
import { useTaskPolling } from "@/features/power_projects/hooks";
import * as api from "../api";
import { ledKeys, useRouteTutorial } from "../hooks";
import type { LedFile, RouteValidation } from "../types";
import { ErrorLine, Field, INPUT, TEXT_OK, TEXT_WARN, errMsg } from "./common";

interface Props {
  leadershipId: string;
  tutorialHref: string;
  hasRoute: boolean;
  /** Rysunek obowiązującej wersji — do pobrania przez obie strony. */
  dxf?: LedFile | null;
  /** Po udanym wgraniu (zadanie w tle skończone) — np. odświeżenie zapytań warsztatu ZE. */
  onUploaded?: () => void;
}

interface LayerReport {
  layer: string;
  status: "objects" | "empty" | "missing";
  lines: number;
  points: number;
}

const LAYER_STATUS: Record<LayerReport["status"], { label: string; cls: string }> = {
  objects: { label: "jest, z obiektami", cls: TEXT_OK },
  empty: { label: "jest, ale pusta", cls: TEXT_WARN },
  missing: { label: "brak w pliku", cls: "text-[var(--color-text-muted)]" },
};
const ORDER: Record<LayerReport["status"], number> = { objects: 0, empty: 1, missing: 2 };

const layersOf = (r: RouteValidation): LayerReport[] => (Array.isArray(r.layers) ? (r.layers as LayerReport[]) : []);
const extraOf = (r: RouteValidation): { layer: string }[] => (Array.isArray(r.extra_layers) ? (r.extra_layers as { layer: string }[]) : []);

/** Wgranie trasy przez firmę: preset warstw → „Sprawdź plik” → wgraj (zadanie w tle,
 *  ten sam potok co u nas: DXF → podgląd → działki z ULDK). */
export function RouteUploadPanel({ leadershipId, tutorialHref, hasRoute, dxf, onUploaded }: Props) {
  const qc = useQueryClient();
  const [preset, setPreset] = useState<string>("");
  const { data: tut } = useRouteTutorial(leadershipId, preset || null);
  const [file, setFile] = useState<File | null>(null);
  const [comment, setComment] = useState("");
  const [validation, setValidation] = useState<RouteValidation | null>(null);
  const [validating, setValidating] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [taskId, setTaskId] = useState<string | null>(null);
  const [open, setOpen] = useState(!hasRoute);
  const task = useTaskPolling(taskId);

  useEffect(() => { if (!preset && tut?.presets?.[0]) setPreset(tut.presets[0].id); }, [tut, preset]);
  useEffect(() => {
    if (task.data?.status !== "success") return;
    qc.invalidateQueries({ queryKey: ledKeys.all });
    // Warsztat ZE (tryb prowadzenia) ma własne zapytania o trasę i działki — odświeża je sam.
    onUploaded?.();
  }, [task.data?.status, qc, onUploaded]);

  const presetName = tut?.presets.find((p) => p.id === preset)?.name ?? "";
  const busy = !!taskId && task.data && !["success", "failure", "cancelled"].includes(task.data.status);
  const withObjects = validation ? Number(validation.with_objects ?? 0) : 0;
  const links = (
    <span className="inline-flex flex-wrap items-center gap-3 text-[11px]">
      <Link to={tutorialHref} className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline"><BookOpen size={12} /> instrukcja warstw</Link>
      {preset && (
        <button type="button" className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline" onClick={() => api.downloadRouteTemplate(leadershipId, preset, presetName)}>
          <Download size={12} /> szablon DXF
        </button>
      )}
      {dxf?.url && (
        <ManagedFileLink file={{ url: dxf.url, original_filename: dxf.name }} className="inline-flex items-center gap-1 text-[var(--color-primary)] hover:underline">
          <FileDown size={12} /> pobierz rysunek trasy
        </ManagedFileLink>
      )}
      {hasRoute && <Button size="sm" variant="ghost" onClick={() => setOpen((v) => !v)}>{open ? "zwiń" : "wgraj nową wersję"}</Button>}
    </span>
  );

  if (!open) {
    return (
      <div className="flex flex-wrap items-center justify-between gap-2 border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-4 py-2">
        <span className="section-label">Trasa (DXF)</span>
        {links}
      </div>
    );
  }
  return (
    <SectionCard title={hasRoute ? "Nowa wersja trasy (DXF)" : "Wgraj rysunek trasy (DXF)"} action={links} bodyClassName="p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
        <Field label="Preset warstw" hint={tut ? `układ projektu: ${tut.zone_label}` : undefined}>
          <select className={INPUT} value={preset} onChange={(e) => { setPreset(e.target.value); setValidation(null); }}>
            {(tut?.presets ?? []).map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
          </select>
        </Field>
        <Field label="Komentarz do wersji" className="sm:col-span-2">
          <input className={INPUT} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="np. wariant A po uzgodnieniu z gminą" />
        </Field>
      </div>
      <FileDropZone files={file ? [file] : []} onFilesChange={(f) => { setFile(f[0] ?? null); setValidation(null); }} accept=".dxf" className="mt-3" label="Przeciągnij plik DXF albo kliknij" />
      <div className="mt-3 flex flex-wrap items-center gap-2">
        <Button size="sm" variant="secondary" disabled={!file || validating} loading={validating} onClick={async () => { if (!file) return; setValidating(true); setError(null); try { setValidation(await api.validateRoute(leadershipId, file, preset || null)); } catch (e) { setError(e); } finally { setValidating(false); } }}>
          Sprawdź plik
        </Button>
        <Button size="sm" disabled={!file || !!busy} onClick={async () => { if (!file) return; setError(null); try { const r = await api.uploadRoute(leadershipId, file, { preset: preset || null, comment }); setTaskId(r.task_id); } catch (e) { setError(e); } }}>
          Wgraj trasę
        </Button>
        {validation && withObjects === 0 && <span className={clsx("text-[12px]", TEXT_WARN)}>Żadna warstwa presetu nie ma obiektów — trasa nie powstanie. Sprawdź nazwy warstw.</span>}
        {error ? <span className="text-[12px] text-[var(--color-primary)]">{errMsg(error)}</span> : null}
      </div>
      {validation && (
        <div className="mt-3 border border-[var(--color-border)] bg-[var(--color-bg-elevated)] p-3 text-[12px]">
          <div className="section-label mb-1">Wynik sprawdzenia{validation.preset_name ? ` · preset ${validation.preset_name}` : ""}</div>
          <p className="mb-2">
            Warstw presetu: <strong className="font-mono">{String(validation.preset_layers_total ?? "")}</strong> · z obiektami: <strong className={clsx("font-mono", TEXT_OK)}>{withObjects}</strong> · puste: <span className="font-mono">{String(validation.empty ?? 0)}</span> · brak w pliku: <span className="font-mono">{String(validation.missing ?? 0)}</span>
          </p>
          <div className="max-h-56 overflow-y-auto border border-[var(--color-border)] bg-[var(--color-bg-surface)]">
            <table className="w-full text-[12px]">
              <thead className="sticky top-0 bg-[var(--color-bg-elevated)]">
                <tr className="text-left font-mono text-[10px] uppercase tracking-widest text-[var(--color-text-muted)]">
                  <th className="px-2 py-1">Warstwa presetu</th><th className="px-2 py-1">W pliku</th><th className="px-2 py-1 text-right">linie</th><th className="px-2 py-1 text-right">punkty</th>
                </tr>
              </thead>
              <tbody>
                {[...layersOf(validation)].sort((a, b) => ORDER[a.status] - ORDER[b.status]).map((l) => (
                  <tr key={l.layer} className="border-t border-[var(--color-border-muted)]">
                    <td className="px-2 py-1 font-mono">{l.layer}</td>
                    <td className={clsx("px-2 py-1", LAYER_STATUS[l.status]?.cls)}>{LAYER_STATUS[l.status]?.label ?? l.status}</td>
                    <td className="px-2 py-1 text-right font-mono tabular-nums">{l.lines}</td>
                    <td className="px-2 py-1 text-right font-mono tabular-nums">{l.points}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {extraOf(validation).length > 0 && <p className="mt-1.5 text-[11px] text-[var(--color-text-muted)]">Warstwy spoza presetu (pominięte): {extraOf(validation).map((l) => l.layer).join(", ")}</p>}
        </div>
      )}
      {taskId && task.data && (
        <div className={clsx("mt-3 border p-3 text-[12px]", task.data.status === "success" ? "border-emerald-600" : task.data.status === "failure" ? "border-[var(--color-primary)]" : "border-[var(--color-border)]")}>
          <div className="font-semibold">
            {task.data.status === "success" ? "Trasa wgrana — działki odczytane z ULDK." : task.data.status === "failure" ? "Wgranie nie powiodło się." : "Przetwarzanie rysunku…"}
          </div>
          {task.data.log && <pre className="mt-1 max-h-40 overflow-auto whitespace-pre-wrap font-mono text-[11px] text-[var(--color-text-secondary)]">{task.data.log}</pre>}
          {task.data.error && <ErrorLine error={{ response: { data: { errors: { detail: task.data.error } } } }} />}
        </div>
      )}
    </SectionCard>
  );
}
